import { useEffect, useState, type MouseEvent } from 'react'
import { useLocation, type Location } from 'react-router-dom'
import { SquareArrowOutUpRight, X } from 'lucide-react'
import { ButtonLink, IconButton, IconButtonLink, Tooltip } from '@/components/play'
import { readPendingInvite } from '@/lib/invites/pendingInvite'
import { cn } from '@/lib/utils'
import './framed-notice.css'

const DISMISSED_KEY = 'streambridge.framedNotice.dismissed'
const POP_OUT_LABEL = 'Pop out to the full view'

/** True when another page shows this one in a frame. A cross-origin parent that blocks reading `top` is a frame too. */
export function isFramed(win: Window = window): boolean {
  try {
    return win.self !== win.top
  } catch {
    return true
  }
}

/**
 * Opens this page as the top window. An embedding page whose sandbox forbids top navigation makes that throw, so the
 * page opens in a new tab instead.
 */
export function popOut(url: string, win: Window = window) {
  try {
    win.top!.location.href = url
  } catch {
    win.open(url, '_blank', 'noopener,noreferrer')
  }
}

const INVITE_PATH = /^\/invite\/?$/

/**
 * Where Pop out goes: this page as the top window. An invite link's token left the address bar on arrival
 * (takeInviteFragment) and is kept in this frame's storage, which a top-level page can't reach when the browser
 * partitions or blocks third-party storage. So on `/invite` the pending token rides along again as the fragment, the
 * link's own form, and the new page's arrival handler takes it as usual. Fragments never reach a server.
 */
export function popOutUrl(location: Pick<Location, 'pathname' | 'search' | 'hash'>, origin = window.location.origin) {
  const page = `${origin}${location.pathname}${location.search}`
  if (INVITE_PATH.test(location.pathname) && !location.hash) {
    const token = readPendingInvite()
    if (token) return `${page}#${token}`
  }
  return `${page}${location.hash}`
}

function readDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function rememberDismissed() {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Storage blocked (private mode, partitioned third-party storage): the tip simply returns on the next load.
  }
}

/** The stacked play tiles with the top tile slipping out of the stack, carrying the pop-out arrow. */
export function PopOutMark({ className }: { className?: string }) {
  return (
    <svg className={cn('framed-mark', className)} viewBox="0 0 26 24" aria-hidden="true" focusable="false">
      <g stroke="var(--neutral-10)" strokeWidth="1.25">
        <rect x="1" y="9" width="14" height="14" rx="3.5" fill="var(--color-1)" />
        <rect x="3.5" y="6.5" width="14" height="14" rx="3.5" fill="var(--color-2)" />
        <rect x="6" y="4" width="14" height="14" rx="3.5" fill="var(--color-3)" />
      </g>
      <g className="framed-mark__escape">
        <rect x="11" y="0.75" width="14" height="14" rx="3.5" fill="var(--neutral-1)" stroke="var(--neutral-10)" strokeWidth="1.25" />
        <path d="M17.5 4.5h4v4M21.5 4.5l-5 5" fill="none" stroke="var(--neutral-10)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  )
}

export type FramedNoticeProps = {
  /** Whether the page is inside a frame. Defaults to detecting it; stories pass it explicitly. */
  framed?: boolean
}

/**
 * A suggestion, not a warning: the host chose to embed the event, but a few things (fullscreen, camera, sign-in
 * pop-ups) work best when StreamBridge is the top window. A thin bar offers Pop out; dismissing it for the session
 * leaves a small pop-out tab on the left edge so viewers can still find the full view.
 */
export function FramedNotice({ framed = isFramed() }: FramedNoticeProps) {
  const location = useLocation()
  const [dismissed, setDismissed] = useState(readDismissed)
  const showBar = framed && !dismissed

  // Full-height pages (the watch screen's fold) subtract the bar while it shows.
  useEffect(() => {
    if (!showBar) return
    const root = document.documentElement
    root.setAttribute('data-framed-notice', '')
    return () => root.removeAttribute('data-framed-notice')
  }, [showBar])

  if (!framed) return null

  const href = popOutUrl(location)
  const onPopOut = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    popOut(href)
  }

  if (dismissed) {
    return (
      <Tooltip content={POP_OUT_LABEL} side="right">
        <IconButtonLink
          href={href}
          target="_top"
          label={POP_OUT_LABEL}
          tooltip={false}
          variant="ghost"
          elevated
          className="play-theme framed-tile"
          onClick={onPopOut}
        >
          <PopOutMark />
        </IconButtonLink>
      </Tooltip>
    )
  }

  return (
    <div className="play-theme framed-notice" role="region" aria-label="Viewing tip">
      <PopOutMark className="framed-notice__mark" />
      <p className="framed-notice__message">
        <span className="framed-notice__long"><b>Insider tip:</b> Some things work better when you pop out.</span>
        <span className="framed-notice__short">Better popped out.</span>
      </p>
      <ButtonLink href={href} target="_top" size="compact" aria-label={POP_OUT_LABEL} className="framed-notice__open" onClick={onPopOut}>
        <SquareArrowOutUpRight />
        <span className="framed-notice__open-text">Pop out</span>
      </ButtonLink>
      <IconButton
        variant="ghost"
        size="compact"
        label="Dismiss tip"
        onClick={() => {
          rememberDismissed()
          setDismissed(true)
        }}
      >
        <X />
      </IconButton>
    </div>
  )
}
